import { AlertTriangle, Clock, CreditCard, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// Friendly, non-scary banner for AI-service failures (out of credits, busy,
// timeout, etc.). Driven by the backend's 4xx responses so a transient AI
// hiccup never looks like a full server crash. Pass the caught axios error.
const STYLES = {
  402: { icon: CreditCard, tone: "amber", title: "AI credits are exhausted",
    fallback: "The AI service has run out of credits. Please add credits and try again." },
  429: { icon: Loader2, tone: "sky", title: "The AI is busy right now",
    fallback: "Too many requests at the moment. Please wait a few seconds and try again." },
  408: { icon: Clock, tone: "amber", title: "The AI took too long",
    fallback: "The AI service took too long to respond. Please try again." },
  _default: { icon: AlertTriangle, tone: "rose", title: "AI service hiccup",
    fallback: "Something went wrong talking to the AI. Please try again in a moment." },
};

const TONE = {
  amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200",
  sky: "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800 text-sky-800 dark:text-sky-200",
  rose: "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-200",
};

export function AiServiceNotice({ error, onRetry, retrying = false, className = "" }) {
  if (!error) return null;
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const cfg = STYLES[status] || STYLES._default;
  const Icon = cfg.icon;
  const message = typeof detail === "string" && detail ? detail : cfg.fallback;
  const spin = cfg.icon === Loader2;

  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-3.5 ${TONE[cfg.tone]} ${className}`}
      data-testid="ai-service-notice"
      role="alert"
    >
      <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${spin ? "animate-spin" : ""}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold" data-testid="ai-service-notice-title">{cfg.title}</div>
        <div className="text-xs mt-0.5 opacity-90" data-testid="ai-service-notice-message">{message}</div>
      </div>
      {onRetry && (
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          disabled={retrying}
          className="rounded-full h-8 shrink-0 bg-white/60 dark:bg-slate-900/40"
          data-testid="ai-service-notice-retry"
        >
          {retrying ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Retry
        </Button>
      )}
    </div>
  );
}

export default AiServiceNotice;
